import React, { useState } from "react";
import styled, { css } from "styled-components";

import MainThumb01 from "assets/png/landingPage/main-thumb-01.png";
import MainThumb02 from "assets/png/landingPage/main-thumb-02.png";
import MainThumb03 from "assets/png/landingPage/main-thumb-03.png";
import MainThumb04 from "assets/png/landingPage/main-thumb-04.png";
import MainThumb05 from "assets/png/landingPage/main-thumb-05.png";
import SpaceManagement from "assets/png/landingPage/space-management.png";
import TrendAnalysis from "assets/png/landingPage/trend-analysis.png";
import Writing from "assets/png/landingPage/writing.png";

import { useDevice } from "utils/device";

const DOCS_ORIGIN = "https://docs.preppaper.com";

const guideItems = [
    {
        id: "prep-start",
        title: "프렙 첫 시작",
        image: MainThumb01,
        links: [
            { label: "스페이스 소개", href: "/docs/prep-start/#prep-start-space" },
            { label: "참고문헌 추가 및 관리", href: "/docs/prep-start/#prep-start-reference" },
            { label: "참고문헌 리뷰 소개", href: "/docs/prep-start/#prep-start-review" },
        ],
        moreHref: "/docs/prep-start/",
    },
    {
        id: "space-management",
        title: "스페이스 관리",
        image: SpaceManagement,
        links: [
            { label: "스페이스", href: "/docs/space-management/#space-management-space" },
            { label: "스페이스와 요금제", href: "/docs/space-management/#space-management-plan" },
            { label: "그룹 스페이스", href: "/docs/space-management/#space-management-group-space" },
        ],
        moreHref: "/docs/space-management/",
    },
    {
        id: "reference-management",
        title: "참고문헌 관리",
        image: MainThumb02,
        links: [
            { label: "참고문헌 추가하기", href: "/docs/reference-management/#reference-management-add" },
            { label: "서지정보 관리하기", href: "/docs/reference-management/#reference-management-metadata" },
            { label: "참고문헌 관리하기", href: "/docs/reference-management/#reference-management-organize" },
        ],
        moreHref: "/docs/reference-management/",
    },
    {
        id: "reference-pdf-review",
        title: "참고문헌 PDF 리뷰",
        tabLabel: "PDF 리뷰",
        image: MainThumb03,
        links: [
            { label: "시작하기", href: "/docs/reference-pdf-review/" },
            { label: "PDF 리뷰 구성", href: "/docs/reference-pdf-review/#reference-pdf-review-layout" },
            { label: "PDF 자동 분석", href: "/docs/reference-pdf-review/#reference-pdf-review-auto-analysis" },
        ],
        moreHref: "/docs/reference-pdf-review/",
    },
    {
        id: "research-note",
        title: "연구노트 관리",
        tabLabel: "연구노트",
        image: MainThumb04,
        links: [
            { label: "새로운 연구노트 만들기", href: "/docs/research-note/#research-note-create" },
            { label: "기본 노트 기능 활용하기", href: "/docs/research-note/#research-note-basic-features" },
            { label: "AI 노트 기능 활용하기", href: "/docs/research-note/#research-note-ai-features" },
        ],
        moreHref: "/docs/research-note/",
    },
    {
        id: "trend-analysis",
        title: "동향분석",
        image: TrendAnalysis,
        links: [
            { label: "연구 주제 및 조건 입력", href: "/docs/trend-analysis/#trend-analysis-topic-input" },
            { label: "연구 계획 수립", href: "/docs/trend-analysis/#trend-analysis-research-plan" },
            { label: "연구 결과 작성", href: "/docs/trend-analysis/#trend-analysis-research-result" },
        ],
        moreHref: "/docs/trend-analysis/",
    },
    {
        id: "paper-writing",
        title: "논문작성",
        image: Writing,
        links: [
            { label: "시작하기", href: "/docs/paper-writing/#paper-writing-start" },
            { label: "기본 작성 방법", href: "/docs/paper-writing/#paper-writing-basics" },
            { label: "자동 번호 및 참조 관리", href: "/docs/paper-writing/#paper-writing-references" },
        ],
        moreHref: "/docs/paper-writing/",
    },
    {
        id: "billing-plan",
        title: "요금제와 결제",
        image: MainThumb05,
        links: [
            { label: "요금제 종류", href: "/docs/billing-plan/#billing-plan-types" },
            { label: "결제 정보 확인", href: "/docs/billing-plan/#billing-plan-payment-info" },
            { label: "결제하기", href: "/docs/billing-plan/#billing-plan-purchase" },
        ],
        moreHref: "/docs/billing-plan/",
    },
];

const toDocsUrl = (path) => `${DOCS_ORIGIN}${path}`;

const Container = styled.section`
    padding: 78px 0 56px;
    width: 100%;
    background: var(--color-White);
    color: var(--color-Black);
    z-index: 1;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            padding: 54px 0 32px;
        `};
`;

const Shell = styled.div`
    margin: 0 auto;
    width: min(1120px, calc(100% - 40px));
`;

const SectionTitle = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 24px;
    margin-bottom: 38px;

    &::after {
        content: "";
        height: 1px;
        background: #f1f1f1;
    }

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            display: block;
            margin-bottom: 28px;

            &::after {
                content: none;
            }
        `};
`;

const Title = styled.h2`
    margin: 0;
    font-size: 36px;
    font-weight: 700;
    line-height: 1.3;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            font-size: 30px;
        `};
`;

const GuideLayout = styled.div`
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    align-items: stretch;
    min-height: 330px;
    border: 1px solid #f1f1f1;
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            grid-template-columns: 1fr;
            min-height: 0;
        `};
`;

const GuideTabs = styled.div`
    display: grid;
    align-content: start;
    gap: 6px;
    padding: 18px 14px;
    border-right: 1px solid #f1f1f1;
    background: #fbfcfd;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            display: flex;
            gap: 8px;
            padding: 14px 14px 12px;
            border-right: 0;
            border-bottom: 1px solid #f1f1f1;
            background: #ffffff;
            overflow-x: auto;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }
        `};
`;

const GuideTab = styled.button`
    width: 100%;
    min-height: 46px;
    padding: 0 14px;
    border: 0;
    border-radius: 6px;
    background: ${(props) => (props.$active ? "#00a878" : "transparent")};
    color: ${(props) => (props.$active ? "#ffffff" : "#475569")};
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    text-align: left;
    cursor: pointer;
    white-space: nowrap;

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$active ? "#00a878" : "#f8fafc")};
        color: ${(props) => (props.$active ? "#ffffff" : "#008464")};
        outline: none;
    }

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            width: auto;
            flex: 0 0 auto;
            min-height: 40px;
            padding: 0 14px;
            border: 1px solid ${(props) => (props.$active ? "#00a878" : "#f1f1f1")};
            border-radius: 999px;
            background: ${(props) => (props.$active ? "#00a878" : "#ffffff")};
            text-align: center;
        `};
`;

const GuideList = styled.div`
    display: grid;
    min-width: 0;
    padding: 34px;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            padding: 22px;
        `};
`;

const GuideItem = styled.article`
    display: grid;
    grid-template-columns: minmax(260px, 0.86fr) minmax(300px, 0.78fr);
    align-items: center;
    gap: 34px;
    min-width: 0;

    &[hidden] {
        display: none;
    }

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            grid-template-columns: 1fr;
            gap: 22px;
            min-height: 0;
        `};
`;

const GuideCopy = styled.div`
    min-width: 0;
`;

const GuideTitle = styled.h3`
    margin: 0 0 22px;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.35;

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            font-size: 23px;
        `};
`;

const LinkList = styled.ul`
    display: grid;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
`;

const GuideLink = styled.a`
    color: #2f3745;
    font-size: 17px;
    text-decoration: none;

    &::before {
        content: "";
        display: inline-block;
        width: 5px;
        height: 5px;
        margin-right: 10px;
        border-radius: 50%;
        background: #cbd5e1;
        vertical-align: middle;
    }

    &:hover,
    &:focus-visible {
        color: #00a878;
    }
`;

const MoreLink = styled.a`
    display: inline-flex;
    margin-top: 28px;
    color: #008464;
    font-weight: 700;
    text-decoration: none;
`;

const GuideImage = styled.img`
    display: block;
    width: 100%;
    max-width: 420px;
    aspect-ratio: 16 / 10;
    object-fit: contain;
    justify-self: end;
    border-radius: 8px;
    background: #f8fafc;
    box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);

    ${(props) =>
        !props.$device.isTabletOrPC &&
        css`
            order: -1;
            justify-self: stretch;
            max-width: none;
        `};
`;

function ServiceIntroSection() {
    const device = useDevice();
    const [activeGuideId, setActiveGuideId] = useState(guideItems[0].id);

    return (
        <Container id="guide" $device={device}>
            <Shell>
                <SectionTitle $device={device}>
                    <Title $device={device}>사용가이드</Title>
                </SectionTitle>

                <GuideLayout $device={device}>
                    <GuideTabs $device={device} role="tablist" aria-label="사용가이드 카테고리">
                        {guideItems.map((item) => {
                            const isActive = item.id === activeGuideId;

                            return (
                                <GuideTab
                                    key={item.id}
                                    id={`guide-tab-${item.id}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    aria-controls={`guide-panel-${item.id}`}
                                    $active={isActive}
                                    $device={device}
                                    onClick={() => setActiveGuideId(item.id)}
                                >
                                    {item.tabLabel || item.title}
                                </GuideTab>
                            );
                        })}
                    </GuideTabs>

                    <GuideList $device={device}>
                        {guideItems.map((item) => {
                            const isActive = item.id === activeGuideId;

                            return (
                                <GuideItem
                                    key={item.id}
                                    id={`guide-panel-${item.id}`}
                                    $device={device}
                                    role="tabpanel"
                                    aria-labelledby={`guide-tab-${item.id}`}
                                    hidden={!isActive}
                                >
                                    <GuideCopy>
                                        <GuideTitle $device={device}>{item.title}</GuideTitle>
                                        <LinkList>
                                            {item.links.map((link) => (
                                                <li key={link.href}>
                                                    <GuideLink
                                                        href={toDocsUrl(link.href)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {link.label}
                                                    </GuideLink>
                                                </li>
                                            ))}
                                        </LinkList>
                                        <MoreLink href={toDocsUrl(item.moreHref)} target="_blank" rel="noreferrer">
                                            더보기 →
                                        </MoreLink>
                                    </GuideCopy>
                                    <GuideImage $device={device} src={item.image} alt="" />
                                </GuideItem>
                            );
                        })}
                    </GuideList>
                </GuideLayout>
            </Shell>
        </Container>
    );
}

export default ServiceIntroSection;
